import { useCachedBannerResponseStore } from "@faire/deprecated-retailer-visitor-shared/components/Banners/CachedBannerResponseStore";
import { useBannersData } from "@faire/deprecated-retailer-visitor-shared/components/Banners/DataFetching/__internal__/useBannersData";
import { LoggedOutCategoryResultsBanner } from "@faire/deprecated-retailer-visitor-shared/components/Banners/LoggedOutCategoryBanners/LoggedOutCategoryResultsBanner";
import { IBannerData } from "@faire/deprecated-retailer-visitor-shared/lib/bannerUtils";
import { useSettingEnableDiscoverCategoryCTABanners } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableDiscoverCategoryCTABanners";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useCategoryPageFilterKeys } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/CategoryPageShareables/hooks/useCategoryPageFilterKeys";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { useLoggedOutCategoryPageCurrentNavigationPath } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedOutCategoryPage/__internal__/Context/useLoggedOutCategoryPageCurrentNavigationPath";
import { ISearchCatNavBannersRequest } from "@faire/web-api--source/indigofair/data/ISearchCatNavBannersRequest";
import { IThreeVariantXP } from "@faire/web-api--source/indigofair/settings/IThreeVariantXP";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { useResponsiveValue } from "@faire/web/ui/hooks/useResponsiveValue";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

const MAX_BANNER_ROWS = 3;
const BANNER_REPEAT_INTERVAL = 5;

export const useLoggedOutCategoryPageBannersResponse = () => {
  const filter_keys = useCategoryPageFilterKeys();
  const navigation_path = useLoggedOutCategoryPageCurrentNavigationPath();
  const discoverCategoryCTABannersVariant =
    useSettingEnableDiscoverCategoryCTABanners();
  const request = ISearchCatNavBannersRequest.build({
    navigation_path,
    filter_keys,
  });
  const { cachedRequest: fetchCategoryPageBanners } =
    useCachedBannerResponseStore(["cachedRequest"]);

  const { data: bannersResponse } = useQuery({
    queryKey: [request],
    queryFn: () => fetchCategoryPageBanners(request),
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });

  // Feature flag to switch between old and new banner logic
  const shouldRepeatInlineBanner =
    discoverCategoryCTABannersVariant === IThreeVariantXP.Bucket.VARIANT_3; // true = new logic (every 3 rows), false = old logic (single banner)

  const firstBannerRow = useResponsiveValue({
    mobileAndAbove: 4,
    tabletAndAbove: 3,
  });
  const secondBannerRow = useResponsiveValue({
    mobileAndAbove: 8,
    tabletAndAbove: 6,
  });

  const multiSignUpProductGridBannerRows = useMemo(() => {
    // New logic: Generate banners every 3 rows, starting from firstBannerRow
    return Array.from(
      { length: MAX_BANNER_ROWS },
      (_, index) => firstBannerRow + index * BANNER_REPEAT_INTERVAL
    ); // Generate 10 banners to cover most pages
  }, [firstBannerRow]);

  // Old logic: Single sign up product grid banner
  const getSignUpProductGridBanner = useCallback((row: number) => {
    return {
      type: LoggedOutCategoryResultsBanner.SIGN_UP_PRODUCT_GRID_BANNER,
      fullRowBanner: true,
      size: 0,
      column: 1,
      row,
    };
  }, []);

  const eligibleBannersDisplayDataInPriorityOrder = useMemo(() => {
    const { filter_pill_banner, sign_up_product_grid_banner } =
      bannersResponse ?? {};

    const possibleInlineBannerRows = shouldRepeatInlineBanner
      ? multiSignUpProductGridBannerRows
      : [firstBannerRow];

    const banners: Array<
      IBannerData<LoggedOutCategoryResultsBanner> | undefined
    > = [];

    if (sign_up_product_grid_banner) {
      possibleInlineBannerRows.forEach((row) => {
        banners.push(getSignUpProductGridBanner(row));
      });
    }

    if (filter_pill_banner) {
      banners.push({
        type: LoggedOutCategoryResultsBanner.FILTER_PILL_TILE,
        size: 1,
        column: 4,
        row: secondBannerRow,
      });
    }

    return banners.filter(isNotUndefined);
  }, [
    bannersResponse,
    shouldRepeatInlineBanner,
    firstBannerRow,
    multiSignUpProductGridBannerRows,
    getSignUpProductGridBanner,
    secondBannerRow,
  ]);

  const { displayableBannersWithAdjustedRow } =
    useBannersData<LoggedOutCategoryResultsBanner>(
      eligibleBannersDisplayDataInPriorityOrder
    );

  return useMemo(
    () => ({
      bannersResponse,
      bannersDisplayData: eligibleBannersDisplayDataInPriorityOrder,
      displayableBannersWithAdjustedRow,
    }),
    [
      bannersResponse,
      eligibleBannersDisplayDataInPriorityOrder,
      displayableBannersWithAdjustedRow,
    ]
  );
};
